import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import './Products.css';

import { useImageUpload } from '../../hooks/useImageUpload';
import { useSelectedLocation } from '../../hooks/useSelectedLocation';
import { useNotification } from '../../context/NotificationContext';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import apiClient from '../../services/api';
import UniversalProductSearch from './UniversalProductSearch';

// Icons as SVG components
const ArrowLeftIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="19" y1="12" x2="5" y2="12"></line>
    <polyline points="12 19 5 12 12 5"></polyline>
  </svg>
);

const UploadIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
    <polyline points="17 8 12 3 7 8"></polyline>
    <line x1="12" y1="3" x2="12" y2="15"></line>
  </svg>
);

const CameraIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
    <circle cx="12" cy="13" r="4"></circle>
  </svg>
);

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"></polyline>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);

const WandIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 4V2"></path>
    <path d="M15 16v-2"></path>
    <path d="M8 9h2"></path>
    <path d="M20 9h2"></path>
    <path d="M17.8 11.8L19 13"></path>
    <path d="M15 9h0"></path>
    <path d="M17.8 6.2L19 5"></path>
    <path d="m3 21 9-9"></path>
    <path d="M12.2 6.2 11 5"></path>
  </svg>
);

const ScissorsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="6" cy="6" r="3"></circle>
    <circle cx="6" cy="18" r="3"></circle>
    <line x1="20" y1="4" x2="8.12" y2="15.88"></line>
    <line x1="14.47" y1="14.48" x2="20" y2="20"></line>
    <line x1="8.12" y1="8.12" x2="12" y2="12"></line>
  </svg>
);

function AddProduct() {
  void useTranslation();
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const selectedLocation = useSelectedLocation();
  const notification = useNotification();

  // Edit mode - check if we have an edit parameter
  const editProductId = searchParams.get('edit');
  const isEditMode = Boolean(editProductId);

  // Get product data from navigation state (passed from Products.jsx)
  const productFromState = location.state?.product;

  const {
    setImageFile,
    localImageUrl,
    setLocalImageUrl,
    imageUrl,
    setImageUrl,
    isUploading,
    reset: resetImageUpload
  } = useImageUpload('product');

  // Form states
  const [retailerSkuId, setRetailerSkuId] = useState('');
  const [productName, setProductName] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [brand, setBrand] = useState('');
  const [variantId, setVariantId] = useState('');
  const [category, setCategory] = useState('');
  const [originalPrice, setOriginalPrice] = useState('');
  const [promoPrice, setPromoPrice] = useState('');
  const [promoStartDate, setPromoStartDate] = useState('');
  const [promoEndDate, setPromoEndDate] = useState('');
  const [promoType, setPromoType] = useState('PRICE_DISCOUNT');
  const [locations, setLocations] = useState(selectedLocation || '');
  const [limitQty, setLimitQty] = useState('');
  const [priceUnit, setPriceUnit] = useState('ea.');
  const [packSize, setPackSize] = useState('');

  // Error states
  const [retailerSkuIdError, setRetailerSkuIdError] = useState('');
  const [productNameError, setProductNameError] = useState('');
  const [originalPriceError, setOriginalPriceError] = useState('');
  const [promoPriceError, setPromoPriceError] = useState('');
  const [promoStartDateError, setPromoStartDateError] = useState('');
  const [promoEndDateError, setPromoEndDateError] = useState('');
  const [promoTypeError, setPromoTypeError] = useState('');

  // Ref to track if we should navigate after save
  const closeAfterSaveRef = useRef(false);

  // Processing states
  const [isScanImageProcessing, setIsScanImageProcessing] = useState(false);
  const [isRemovingBackground, setIsRemovingBackground] = useState(false);
  const [processedImageUrl, setProcessedImageUrl] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [isLoadingProduct, setIsLoadingProduct] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Universal product catalog search
  const [selectedUniversalProduct, setSelectedUniversalProduct] = useState(null);
  const [universalProductId, setUniversalProductId] = useState(null);

  // Populate form with product data (from state or API)
  const populateFormWithProduct = (product) => {
    setRetailerSkuId(product.retailer_sku_id || '');
    setProductName(product.product_name || '');
    setProductDescription(product.product_description || '');
    setBrand(product.brand || '');
    setVariantId(product.variant_id || '');
    setCategory(product.category || '');
    setOriginalPrice(product.original_price?.toString() || '');
    setPromoPrice(product.promo_price?.toString() || '');
    setPromoStartDate(product.promo_start_date ? product.promo_start_date.split('T')[0] : '');
    setPromoEndDate(product.promo_end_date ? product.promo_end_date.split('T')[0] : '');
    setPromoType(product.promo_type || 'PRICE_DISCOUNT');
    setLocations(product.locations || selectedLocation || '');
    setLimitQty(product.limit_qty?.toString() || '');
    setPriceUnit(product.price_unit || 'ea.');
    setPackSize(product.pack_size || '');

    // Set image if exists
    if (product.original_img_large_src) {
      setLocalImageUrl(product.original_img_large_src);
      setProcessedImageUrl(product.original_img_large_src);
    }
  };

  // Load product data when in edit mode
  useEffect(() => {
    if (editProductId) {
      // First try to use product data from navigation state
      if (productFromState) {
        populateFormWithProduct(productFromState);
      } else {
        // Fallback to fetching from API if state is not available
        fetchProductForEdit(editProductId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editProductId, productFromState]);

  useEffect(() => {
    if (selectedLocation && !isEditMode) {
      setLocations(selectedLocation);
    }
  }, [selectedLocation, isEditMode]);

  const fetchProductForEdit = async (productId) => {
    try {
      setIsLoadingProduct(true);
      const response = await apiClient.get(`/api/products/${productId}`);
      const product = response.data;
      populateFormWithProduct(product);
    } catch (error) {
      notification.error('Failed to load product data');
      navigate('/products');
    } finally {
      setIsLoadingProduct(false);
    }
  };


  const handleImageUpload = (files) => {
    if (!files || !files[0]) return;
    const file = files[0];
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setLocalImageUrl(e.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleImageUpload(e.dataTransfer.files);
    }
  };

  const handleTakePhoto = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment';
    input.onchange = (e) => {
      if (e.target.files) {
        handleImageUpload(e.target.files);
      }
    };
    input.click();
  };

  const processScanImage = async () => {
    if (!imageUrl) {
      notification.error('Please upload an image first');
      return;
    }

    try {
      setIsScanImageProcessing(true);
      const token = await getAccessTokenSilently();
      const response = await axios.post(`${process.env.REACT_APP_SERVER_DOMAIN}/api/textract/process-image`, {
        imageUrl: imageUrl
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data) {
        if (response.data.productName) {
          setProductName(response.data.productName);
          setProductDescription(response.data.productName);
        }
        if (response.data.brandName) setBrand(response.data.brandName);
        if (response.data.originalPrice) setOriginalPrice((parseFloat(response.data.originalPrice) / 100).toString());
        if (response.data.promoPrice) setPromoPrice((parseFloat(response.data.promoPrice) / 100).toString());
        notification.success('Image processed successfully!');
      }
    } catch (error) {
      console.error('Error processing image:', error);
      notification.error('Failed to process image');
    } finally {
      setIsScanImageProcessing(false);
    }
  };

  const removeBackground = async () => {
    const imgUrl = imageUrl || localImageUrl;
    if (!imgUrl) {
      notification.error('Please upload an image first');
      return;
    }

    try {
      setIsRemovingBackground(true);
      const token = await getAccessTokenSilently();
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_DOMAIN}/api/image-processing/remove-background-url`,
        { imageUrl: imgUrl, upload: true },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data && response.data.success) {
        setLocalImageUrl(response.data.imageUrl);
        setProcessedImageUrl(response.data.imageUrl);
        notification.success('Background removed successfully!');
      } else {
        throw new Error(response.data?.error || 'Failed to remove background');
      }
    } catch (error) {
      console.error('Error removing background:', error);
      notification.error(error.response?.data?.error || error.message || 'Failed to remove background');
    } finally {
      setIsRemovingBackground(false);
    }
  };

  // Category mapping from universal product categories to local categories
  const mapUniversalCategory = (universalCategory) => {
    if (!universalCategory) return '';
    const mapping = {
      'dairy & eggs': 'dairy',
      'produce': 'produce',
      'meat & seafood': 'meat',
      'bakery': 'bakery',
      'beverages': 'beverages',
      'pantry': 'pantry',
      'snacks': 'snacks',
      'frozen foods': 'frozen',
    };
    return mapping[universalCategory.toLowerCase()] || '';
  };

  const handleUniversalProductSelected = async (universalProduct) => {
    setSelectedUniversalProduct(universalProduct);
    setUniversalProductId(universalProduct._id);

    // Auto-fill form fields
    if (universalProduct.name) {
      setProductName(universalProduct.name);
      setProductDescription(universalProduct.name);
    }
    if (universalProduct.brand) setBrand(universalProduct.brand);
    setCategory(mapUniversalCategory(universalProduct.category));
    if (universalProduct.size) setPackSize(universalProduct.size);
    if (universalProduct.msrp) setOriginalPrice(universalProduct.msrp.toString());
    if (universalProduct.upc) setRetailerSkuId(universalProduct.upc);

    // Transfer image to retailer's S3 via server-side proxy (avoids CORS)
    if (universalProduct.primaryImageUrl) {
      try {
        const uploadRes = await apiClient.post('/api/s3/upload-from-url', {
          url: universalProduct.primaryImageUrl,
          type: 'product',
        });
        const s3Url = uploadRes.data.url;
        setLocalImageUrl(s3Url);
        setImageUrl(s3Url);
      } catch {
        notification.error('Failed to load product image. You can upload one manually.');
      }
    }

    notification.success('Product details filled from catalog');
  };

  const handleClearUniversalProduct = () => {
    setSelectedUniversalProduct(null);
    setUniversalProductId(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validation
    if (retailerSkuId.trim() === '') {
      setRetailerSkuIdError('SKU is required');
      return;
    }
    if (productName.trim() === '') {
      setProductNameError('Product name is required');
      return;
    }
    if (originalPrice.trim() === '') {
      setOriginalPriceError('Original price is required');
      return;
    }

    if (promoPrice.trim() !== '') {
      if (Number(promoPrice) > Number(originalPrice)) {
        setPromoPriceError('Promo price should be less than original price');
        return;
      }
      if (!promoStartDate) {
        setPromoStartDateError('Start date is required for promo');
        return;
      }
      if (!promoEndDate) {
        setPromoEndDateError('End date is required for promo');
        return;
      }
      if (new Date(promoEndDate) < new Date(promoStartDate)) {
        setPromoEndDateError('End date must be after start date');
        return;
      }
      if (promoType.trim() === '') {
        setPromoTypeError('Promo type is required');
        return;
      }
    }

    try {
      const token = await getAccessTokenSilently();

      if (isEditMode) {
        // Update existing product
        const productUpdate = {
          retailer_sku_id: retailerSkuId,
          product_name: productName,
          product_description: productDescription,
          variant_id: variantId,
          original_price: originalPrice,
          promo_price: promoPrice.trim() === '' ? null : promoPrice,
          promo_start_date: promoStartDate || null,
          promo_end_date: promoEndDate || null,
          promo_type: promoType,
          locations,
          original_img_large_src: processedImageUrl || imageUrl,
          brand,
          category,
          limit_qty: limitQty || null,
          price_unit: priceUnit,
          pack_size: packSize,
          universal_product_id: universalProductId || undefined,
        };

        await apiClient.put(`/api/products/${editProductId}`, productUpdate);
        notification.success('Product updated successfully!');
      } else {
        // Create new product
        const product = {
          retailerSkuId,
          productName,
          productDescription,
          variantId,
          originalPrice,
          promoPrice: promoPrice.trim() === '' ? null : promoPrice,
          promoStartDate: promoStartDate || null,
          promoEndDate: promoEndDate || null,
          promoType,
          locations,
          imageUrl: processedImageUrl || imageUrl,
          brand,
          category,
          limitQty: limitQty || null,
          priceUnit,
          packSize,
          universalProductId: universalProductId || undefined,
        };

        await axios.post(`${process.env.REACT_APP_SERVER_DOMAIN}/api/products/`, product, {
          headers: { Authorization: `Bearer ${token}` },
        });

        notification.success('Product saved successfully!');
      }

      if (closeAfterSaveRef.current) {
        closeAfterSaveRef.current = false;
        navigate('/products');
      }
    } catch (error) {
      closeAfterSaveRef.current = false;
      const errorMessage = error.response?.data?.error || 'Unknown error';
      notification.error(`Failed to ${isEditMode ? 'update' : 'save'} product: ${errorMessage}`);
    }
  };

  const clearForm = () => {
    resetImageUpload();
    setProcessedImageUrl(null);
    setSelectedUniversalProduct(null);
    setUniversalProductId(null);
    setRetailerSkuId('');
    setProductName('');
    setProductDescription('');
    setBrand('');
    setVariantId('');
    setCategory('');
    setOriginalPrice('');
    setPromoPrice('');
    setPromoStartDate('');
    setPromoEndDate('');
    setPromoType('PRICE_DISCOUNT');
    setLocations(selectedLocation || '');
    setLimitQty('');
    setPriceUnit('ea.');
    setPackSize('');
    setRetailerSkuIdError('');
    setProductNameError('');
    setOriginalPriceError('');
    setPromoPriceError('');
    setPromoStartDateError('');
    setPromoEndDateError('');
    setPromoTypeError('');
  };

  const calculateSavings = () => {
    if (promoPrice && originalPrice && promoType !== 'BOGO') {
      const savings = Math.round((1 - parseFloat(promoPrice) / parseFloat(originalPrice)) * 100);
      if (savings > 0) return `Save ${savings}%`;
    }
    return null;
  };

  const isProcessing = isScanImageProcessing || isRemovingBackground || isUploading;

  const handleDelete = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteDialogOpen(false);
    try {
      await apiClient.delete(`/api/products/${editProductId}`);
      notification.success('Product deleted successfully!');
      navigate('/products');
    } catch (error) {
      console.error('Error deleting product:', error);
      notification.error('Failed to delete product');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  return (
    <div className="add-product-container">

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete this product?"
        description="This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Toolbar */}
      <div className="add-product-toolbar">
        <div className="toolbar-left">
          <button className="products-back-btn" onClick={() => navigate('/products')}>
            <ArrowLeftIcon />
          </button>
          <div className="toolbar-title">
            <h1>{isEditMode ? 'Edit Product' : 'Add New Product'}</h1>
            <p>{isEditMode ? 'Update product details' : 'Enter product details and upload images'}</p>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="add-product-content" style={isLoadingProduct ? { display: 'flex', alignItems: 'center', justifyContent: 'center' } : {}}>
        {isLoadingProduct ? (
          <p>Loading product...</p>
        ) : (
        <div className="add-product-form-wrapper">
          <form onSubmit={handleSubmit} id="add-product-form">
            {/* Universal Product Catalog Search - only in create mode */}
            {!isEditMode && (
              <UniversalProductSearch
                onProductSelected={handleUniversalProductSelected}
                onClear={handleClearUniversalProduct}
                selectedProduct={selectedUniversalProduct}
              />
            )}

            {/* Product Information Section */}
            <div className="modern-card">
              <div className="card-header">
                <h2>Product Information</h2>
              </div>
              <div className="card-body">
                <div className="form-row form-row-2">
                  <div className="form-group">
                    <label className="form-label">
                      SKU <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      value={retailerSkuId}
                      onChange={(e) => {
                        setRetailerSkuId(e.target.value);
                        setRetailerSkuIdError('');
                      }}
                      placeholder="e.g., 1922920252"
                    />
                    {retailerSkuIdError && <span className="form-hint" style={{ color: '#dc2626' }}>{retailerSkuIdError}</span>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Variant ID</label>
                    <input
                      type="text"
                      className="form-input"
                      value={variantId}
                      onChange={(e) => setVariantId(e.target.value)}
                      placeholder="e.g., VAR-001"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      Product Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      value={productName}
                      onChange={(e) => {
                        setProductName(e.target.value);
                        setProductNameError('');
                      }}
                      placeholder="e.g., Organic Wheat Bread"
                    />
                    {productNameError && <span className="form-hint" style={{ color: '#dc2626' }}>{productNameError}</span>}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-textarea"
                      rows={3}
                      value={productDescription}
                      onChange={(e) => setProductDescription(e.target.value)}
                      placeholder="Enter product description..."
                    />
                  </div>
                </div>

                <div className="form-row form-row-2">
                  <div className="form-group">
                    <label className="form-label">Category</label>
                    <select
                      className="form-select"
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                    >
                      <option value="">Select category</option>
                      <option value="bakery">Bakery</option>
                      <option value="dairy">Dairy</option>
                      <option value="produce">Produce</option>
                      <option value="meat">Meat & Seafood</option>
                      <option value="frozen">Frozen Foods</option>
                      <option value="pantry">Pantry</option>
                      <option value="beverages">Beverages</option>
                      <option value="snacks">Snacks</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Brand</label>
                    <input
                      type="text"
                      className="form-input"
                      value={brand}
                      onChange={(e) => setBrand(e.target.value)}
                      placeholder="e.g., Nature's Best"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Pricing Section */}
            <div className="modern-card">
              <div className="card-header">
                <h2>Pricing</h2>
              </div>
              <div className="card-body">
                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label className="form-label">
                      Original Price <span className="required">*</span>
                    </label>
                    <div className="form-input-with-prefix">
                      <span className="form-input-prefix">$</span>
                      <input
                        type="number"
                        step="0.01"
                        className="form-input"
                        value={originalPrice}
                        onChange={(e) => {
                          setOriginalPrice(e.target.value);
                          setOriginalPriceError('');
                        }}
                        placeholder="0.00"
                      />
                    </div>
                    {originalPriceError && <span className="form-hint" style={{ color: '#dc2626' }}>{originalPriceError}</span>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Unit</label>
                    <select
                      className="form-select"
                      value={priceUnit}
                      onChange={(e) => setPriceUnit(e.target.value)}
                    >
                      <option value="ea.">Each</option>
                      <option value="lb.">Per lb</option>
                      <option value="kg">Per kg</option>
                      <option value="oz">Per oz</option>
                      <option value="ltr">Per ltr</option>
                      <option value="pack">Pack</option>
                      <option value="box">Box</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Pack Size / Weight</label>
                    <input
                      type="text"
                      className="form-input"
                      value={packSize}
                      onChange={(e) => setPackSize(e.target.value)}
                      placeholder="e.g., 16 oz, 1 lb"
                    />
                  </div>
                </div>

                <div className="promo-section">
                  <h3 className="form-section-title">Promotional Pricing (Optional)</h3>

                  <div className="form-row form-row-3">
                    <div className="form-group">
                      <label className="form-label">Promo Type</label>
                      <select
                        className="form-select"
                        value={promoType}
                        onChange={(e) => {
                          setPromoType(e.target.value);
                          setPromoTypeError('');
                        }}
                      >
                        <option value="PRICE_DISCOUNT">$ off on Original Price</option>
                        <option value="PERCENTAGE_DISCOUNT">% off on Original Price</option>
                        <option value="BOGO">Buy 1 Get 1 Free</option>
                      </select>
                      {promoTypeError && <span className="form-hint" style={{ color: '#dc2626' }}>{promoTypeError}</span>}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Promo Start Date</label>
                      <input
                        type="date"
                        className="form-input"
                        value={promoStartDate}
                        onChange={(e) => {
                          setPromoStartDate(e.target.value);
                          setPromoStartDateError('');
                        }}
                      />
                      {promoStartDateError && <span className="form-hint" style={{ color: '#dc2626' }}>{promoStartDateError}</span>}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Promo End Date</label>
                      <input
                        type="date"
                        className="form-input"
                        value={promoEndDate}
                        onChange={(e) => {
                          setPromoEndDate(e.target.value);
                          setPromoEndDateError('');
                        }}
                      />
                      {promoEndDateError && <span className="form-hint" style={{ color: '#dc2626' }}>{promoEndDateError}</span>}
                    </div>
                  </div>

                  <div className="form-row form-row-2">
                    <div className="form-group">
                      <label className="form-label">Promo Price</label>
                      <div className="form-input-with-prefix">
                        <span className="form-input-prefix">$</span>
                        <input
                          type="number"
                          step="0.01"
                          className="form-input"
                          value={promoPrice}
                          onChange={(e) => {
                            setPromoPrice(e.target.value);
                            setPromoPriceError('');
                          }}
                          placeholder="0.00"
                          disabled={promoType === 'BOGO'}
                        />
                      </div>
                      {promoPriceError && <span className="form-hint" style={{ color: '#dc2626' }}>{promoPriceError}</span>}
                      {calculateSavings() && (
                        <span className="form-hint form-hint-success">{calculateSavings()}</span>
                      )}
                      {promoType === 'BOGO' && (
                        <span className="form-hint form-hint-info">Promo price not needed for BOGO</span>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Limit Quantity</label>
                      <input
                        type="number"
                        min="0"
                        className="form-input"
                        value={limitQty}
                        onChange={(e) => setLimitQty(e.target.value)}
                        placeholder="e.g., 5"
                      />
                      <span className="form-hint">Max items per customer</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Image Upload Section */}
            <div className="modern-card">
              <div className="card-header">
                <h2>Product Images</h2>
              </div>
              <div className="card-body">
                {!localImageUrl ? (
                  <div
                    className={`drag-drop-area ${dragActive ? 'active' : ''}`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                  >
                    <div className="drag-drop-icon">
                      <UploadIcon />
                    </div>
                    <div className="drag-drop-text">
                      <h3>Add product image</h3>
                      <p>Drag and drop, upload from device, or take a photo</p>
                    </div>
                    <div className="drag-drop-buttons">
                      <label className="btn-modern btn-outline-purple" style={{ cursor: 'pointer' }}>
                        <input
                          type="file"
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={(e) => handleImageUpload(e.target.files)}
                        />
                        <UploadIcon /> Upload Photo
                      </label>
                      <button
                        type="button"
                        className="btn-modern btn-outline-purple"
                        onClick={handleTakePhoto}
                      >
                        <CameraIcon /> Take Photo
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ maxWidth: '400px', margin: '0 auto' }}>
                    <div className="image-preview">
                      <div className="image-preview-container">
                        <img
                          src={localImageUrl}
                          alt="Product"
                          className="image-preview-img"
                        />
                        <div className="image-actions">
                          <button
                            type="button"
                            className="image-action-btn image-action-clear"
                            onClick={() => {
                              setLocalImageUrl('');
                              setProcessedImageUrl(null);
                              resetImageUpload();
                            }}
                          >
                            <TrashIcon /> Clear
                          </button>
                          <button
                            type="button"
                            className="image-action-btn image-action-process"
                            onClick={removeBackground}
                            disabled={isProcessing || (!imageUrl && !localImageUrl)}
                          >
                            <ScissorsIcon /> {isRemovingBackground ? 'Removing...' : 'Remove BG'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

          </form>
        </div>
        )}
      </div>

      {/* Fixed Action Bar */}
      <div className="action-bar">
        <button
          type="button"
          className="action-btn secondary"
          onClick={() => navigate('/products')}
        >
          Cancel
        </button>
        <button
          type="submit"
          form="add-product-form"
          className="action-btn primary"
          disabled={isProcessing || isLoadingProduct}
        >
          {isEditMode ? 'Update' : 'Save'}
        </button>
        <button
          type="button"
          className="action-btn primary-dark"
          onClick={() => {
            closeAfterSaveRef.current = true;
            const form = document.getElementById('add-product-form');
            if (form) form.requestSubmit();
          }}
          disabled={isProcessing || isLoadingProduct}
        >
          {isEditMode ? 'Update & Close' : 'Save & Close'}
        </button>
        {isEditMode && (
          <button
            type="button"
            className="action-btn danger"
            onClick={handleDelete}
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

export default AddProduct;
